/**
 * Iter 249 — End-of-call dialog for the Aircall Live Translator.
 *
 * When the agent ends a session, this modal:
 *  1. Calls /aircall/translator/sessions/{id}/analyze (GPT-5.2 extracts contact data + summary)
 *  2. Shows editable fields (name, email, phone, company, summary, next steps, sentiment)
 *  3. Lists matching prospects (with score)
 *  4. Agent picks: "Link to existing" / "Create new prospect" / "Skip"
 *  5. POST /aircall/translator/sessions/{id}/link-prospect → persists note + link
 */
import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Loader2, UserCheck, UserPlus, X, Sparkles, ChevronRight, Search } from "lucide-react";
import { toast } from "sonner";

const SENTIMENTS = [
  { value: "positive", label: "😊 Positif", color: "text-emerald-400 border-emerald-500/30" },
  { value: "neutral", label: "😐 Neutre", color: "text-slate-300 border-slate-500/30" },
  { value: "negative", label: "😟 Négatif", color: "text-rose-400 border-rose-500/30" },
];

export default function EndOfCallDialog({ api, session, onClose, onLinked }) {
  const [loading, setLoading] = useState(true);
  const [analysis, setAnalysis] = useState(null);
  const [extracted, setExtracted] = useState({
    first_name: "", last_name: "", email: "", phone: "", company: "",
    job_title: "", summary: "", next_steps: [], sentiment: "neutral", topics: [],
  });
  const [matches, setMatches] = useState([]);
  const [chosenMatch, setChosenMatch] = useState(null);
  const [saving, setSaving] = useState(false);
  const [nextStepInput, setNextStepInput] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.post(`/aircall/translator/sessions/${session.session_id}/analyze`);
        if (cancelled) return;
        setAnalysis(r.data);
        setExtracted({
          first_name: r.data.extracted.first_name || "",
          last_name: r.data.extracted.last_name || "",
          email: r.data.extracted.email || "",
          phone: r.data.extracted.phone || "",
          company: r.data.extracted.company || "",
          job_title: r.data.extracted.job_title || "",
          summary: r.data.extracted.summary || "",
          next_steps: r.data.extracted.next_steps || [],
          sentiment: r.data.extracted.sentiment || "neutral",
          topics: r.data.extracted.topics || [],
        });
        setMatches(r.data.matches || []);
        // Auto-select the best match if score >= 0.6
        if (r.data.matches && r.data.matches.length && r.data.matches[0].match_score >= 0.6) {
          setChosenMatch(r.data.matches[0]);
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || "Analyse impossible");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api, session.session_id]);

  const updateField = (k, v) => setExtracted((p) => ({ ...p, [k]: v }));

  const addNextStep = () => {
    const s = nextStepInput.trim();
    if (!s) return;
    setExtracted((p) => ({ ...p, next_steps: [...(p.next_steps || []), s] }));
    setNextStepInput("");
  };

  const removeNextStep = (i) => {
    setExtracted((p) => ({ ...p, next_steps: p.next_steps.filter((_, idx) => idx !== i) }));
  };

  const handleSubmit = async (mode) => {
    setSaving(true);
    try {
      const payload = {
        mode,
        summary: extracted.summary,
        next_steps: extracted.next_steps,
        sentiment: extracted.sentiment,
        topics: extracted.topics,
      };
      if (mode === "link") {
        if (!chosenMatch) {
          toast.error("Sélectionnez d'abord un prospect à lier");
          setSaving(false);
          return;
        }
        payload.prospect_id = chosenMatch.prospect_id;
      } else if (mode === "create") {
        payload.prospect_data = {
          first_name: extracted.first_name,
          last_name: extracted.last_name,
          email: extracted.email,
          phone: extracted.phone,
          company: extracted.company,
          job_title: extracted.job_title,
        };
      }
      const r = await api.post(`/aircall/translator/sessions/${session.session_id}/link-prospect`, payload);
      if (mode === "link") toast.success("Conversation liée à la fiche client");
      else if (mode === "create") toast.success(`Prospect "${extracted.first_name} ${extracted.last_name}" créé`);
      else toast.info("Conversation non liée (ignorée)");
      onLinked?.(r.data);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="end-of-call-dialog">
      <div className="bg-[#14101c] border border-white/10 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/5 sticky top-0 bg-[#14101c] z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Synthèse IA & fiche client</h2>
              <p className="text-xs text-slate-400">Vérifiez les informations puis liez la conversation au CRM</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1" data-testid="close-eoc">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-10 flex flex-col items-center justify-center text-slate-400 gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
            <div className="text-sm">L'IA analyse la conversation et cherche dans votre CRM…</div>
          </div>
        ) : (
          <div className="p-5 space-y-5">
            {/* Matches section */}
            {matches.length > 0 ? (
              <div className="space-y-2">
                <div className="text-[11px] uppercase tracking-wider text-emerald-400 font-bold flex items-center gap-1.5">
                  <UserCheck className="w-3.5 h-3.5" />
                  {matches.length} fiche{matches.length > 1 ? "s" : ""} client correspondante{matches.length > 1 ? "s" : ""} trouvée{matches.length > 1 ? "s" : ""}
                </div>
                <div className="grid grid-cols-1 gap-2">
                  {matches.map((m) => (
                    <button
                      key={m.prospect_id}
                      onClick={() => setChosenMatch(m)}
                      className={`w-full text-left p-3 rounded border transition-colors ${chosenMatch?.prospect_id === m.prospect_id
                        ? "border-emerald-500/50 bg-emerald-500/10"
                        : "border-white/10 bg-white/[0.02] hover:border-white/20"}`}
                      data-testid={`match-${m.prospect_id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-white font-medium truncate">
                            {m.first_name || ""} {m.last_name || ""}
                            {m.company && <span className="text-slate-500 font-normal"> · {m.company}</span>}
                          </div>
                          <div className="text-[11px] text-slate-400 mt-0.5 truncate">
                            {m.email || "—"} · {m.phone || "—"} · {m.status || "—"}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300">
                            {Math.round(m.match_score * 100)}%
                          </span>
                          {chosenMatch?.prospect_id === m.prospect_id && (
                            <ChevronRight className="w-4 h-4 text-emerald-400" />
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-amber-500/5 border border-amber-500/20 rounded p-3 text-xs text-amber-300 flex items-center gap-2">
                <Search className="w-3.5 h-3.5" />
                Aucune fiche existante. Vous pouvez créer un nouveau prospect ci-dessous.
              </div>
            )}

            {/* Extracted contact info — editable */}
            <div className="space-y-3">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">
                Informations contact (extraites par l'IA — éditables)
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Prénom" value={extracted.first_name} onChange={(v) => updateField("first_name", v)} testid="eoc-first-name" />
                <Field label="Nom" value={extracted.last_name} onChange={(v) => updateField("last_name", v)} testid="eoc-last-name" />
                <Field label="Email" value={extracted.email} onChange={(v) => updateField("email", v)} testid="eoc-email" type="email" />
                <Field label="Téléphone" value={extracted.phone} onChange={(v) => updateField("phone", v)} testid="eoc-phone" />
                <Field label="Société" value={extracted.company} onChange={(v) => updateField("company", v)} testid="eoc-company" />
                <Field label="Poste / titre" value={extracted.job_title} onChange={(v) => updateField("job_title", v)} testid="eoc-job-title" />
              </div>
            </div>

            {/* Summary */}
            <div className="space-y-2">
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">
                Résumé de l'appel (IA)
              </label>
              <textarea
                value={extracted.summary}
                onChange={(e) => updateField("summary", e.target.value)}
                rows={4}
                className="w-full bg-[#0b0811] border border-white/10 rounded px-3 py-2 text-sm text-white"
                placeholder="Résumé de la conversation…"
                data-testid="eoc-summary"
              />
            </div>

            {/* Next steps */}
            <div className="space-y-2">
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">
                Prochaines étapes
              </label>
              {extracted.next_steps.length > 0 && (
                <ul className="space-y-1">
                  {extracted.next_steps.map((s, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-white bg-white/[0.03] border border-white/5 rounded px-2.5 py-1.5">
                      <span className="flex-1">{s}</span>
                      <button onClick={() => removeNextStep(i)} className="text-slate-500 hover:text-rose-400" data-testid={`remove-step-${i}`}>
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex gap-2">
                <input
                  value={nextStepInput}
                  onChange={(e) => setNextStepInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addNextStep()}
                  placeholder="Ajouter une action de suivi…"
                  className="flex-1 bg-[#0b0811] border border-white/10 rounded px-3 py-1.5 text-sm text-white"
                  data-testid="eoc-next-step-input"
                />
                <Button onClick={addNextStep} size="sm" variant="outline" data-testid="eoc-add-step">+</Button>
              </div>
            </div>

            {/* Sentiment */}
            <div className="space-y-2">
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">
                Sentiment perçu
              </label>
              <div className="flex gap-2 flex-wrap">
                {SENTIMENTS.map((s) => (
                  <button
                    key={s.value}
                    onClick={() => updateField("sentiment", s.value)}
                    className={`px-3 py-1.5 rounded border text-xs transition-colors ${extracted.sentiment === s.value
                      ? `${s.color} bg-white/5`
                      : "border-white/10 text-slate-500 hover:border-white/20"}`}
                    data-testid={`eoc-sentiment-${s.value}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {analysis?.session && (
              <div className="text-[10px] text-slate-600 border-t border-white/5 pt-3">
                Session {session.session_id} · {analysis.session.exchanges_count} échanges traduits
                {" · "}{(analysis.session.source_lang || "?").toUpperCase()} ↔ {(analysis.session.target_lang || "?").toUpperCase()}
              </div>
            )}
          </div>
        )}

        {/* Footer actions */}
        {!loading && (
          <div className="p-5 border-t border-white/5 sticky bottom-0 bg-[#14101c] flex items-center justify-between gap-2 flex-wrap">
            <Button
              variant="ghost"
              onClick={() => handleSubmit("skip")}
              disabled={saving}
              className="text-slate-400 hover:text-white"
              data-testid="eoc-skip"
            >
              Ignorer (ne rien lier)
            </Button>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                onClick={() => handleSubmit("link")}
                disabled={saving || !chosenMatch}
                className="bg-emerald-500 hover:bg-emerald-400 text-[#14101c] font-bold"
                data-testid="eoc-link"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserCheck className="w-4 h-4 mr-2" />}
                Lier à la fiche sélectionnée
              </Button>
              <Button
                onClick={() => handleSubmit("create")}
                disabled={saving || !(extracted.first_name || extracted.last_name || extracted.phone || extracted.email)}
                className="bg-purple-500 hover:bg-purple-400 text-white"
                data-testid="eoc-create"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
                Créer un nouveau prospect
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">{label}</label>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#0b0811] border border-white/10 rounded px-2.5 py-1.5 text-sm text-white"
        data-testid={testid}
      />
    </div>
  );
}
